import { useCallback, useEffect, useState } from 'react'
import api from '../../../helpers/apiRequester'

export interface FundableClientsResponse {
    introducer: string
    fundableClients: number
    totalClients: number
    byReason: Record<string, number>
    calculatedAt: string
    durationMs: number
}

interface UseFundableClientsResult {
    data: FundableClientsResponse | null
    loading: boolean
    error: string | null
    reload: () => void
}

// Shared fetch for the `/introducer-fundable-clients` endpoint. Used by both
// the detailed FundableClientsStat card (Sentinel users) and StatsView's
// "Clients with Claims" → "Fundable Clients" tile swap (regular users), so the
// fetch/parse logic lives in one place. `enabled` lets callers skip the (heavy)
// request when the number isn't going to be shown.
export function useFundableClients(introducer?: string, enabled = true, cohort?: string): UseFundableClientsResult {
    const shouldFetch = enabled && !!introducer
    const [loading, setLoading] = useState<boolean>(shouldFetch)
    const [error, setError] = useState<string | null>(null)
    const [data, setData] = useState<FundableClientsResponse | null>(null)

    const load = useCallback(async () => {
        if (!enabled || !introducer) return
        setLoading(true)
        setError(null)
        try {
            const res = await api({
                url: '/introducer-fundable-clients',
                // `cohort` scopes the count to a PCP Referral campaign window when
                // supplied; omitted for other introducers (backend defaults to all).
                data: cohort ? { introducer, cohort } : { introducer },
            })
            if (res.data?.success && res.data?.data) {
                setData(res.data.data as FundableClientsResponse)
            } else {
                setError(res.data?.message || 'Failed to load fundable clients')
            }
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Failed to load fundable clients')
        } finally {
            setLoading(false)
        }
    }, [introducer, enabled, cohort])

    useEffect(() => {
        if (shouldFetch) load()
    }, [load, shouldFetch])

    return { data, loading, error, reload: load }
}
